import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import {
  Booking,
  BookingChange,
  newGuideInterface,
} from '../../../core/models';
import { TimestampToDatePipe } from '../../../core/timestamp-to-date.pipe';
import {
  BusCompanyMap,
  JettyMap,
  StornoMap,
} from '../../../core/bookingParameters';
import { getLatestChange, sortGuides } from '../../../core/booking-from.utils';

@Component({
  selector: 'app-booking-pdf-html',
  standalone: true,
  template: `
    <div class="container mx-auto p-6" *ngIf="booking">
      <div class="flex flex-col justify-around">
        <!-- Booking Details -->
        <div class="mb-6 flex flex-row justify-between">
          <div class="flex flex-col">
            <!-- Ship Information -->
            <div class="mb-2">
              <h2 class="text-base font-bold text-gray-800">
                Schiff Information
              </h2>
              <div class="mt-1">
                <p class="text-xs text-gray-600">
                  <span class="font-semibold text-xs">Unternehmen:</span><br />
                  {{ booking.ship.companyName }}
                </p>
                <p class="text-xs text-gray-600">
                  <span class="font-semibold text-xs">Schiffname:</span><br />
                  {{ booking.ship.shipName }}
                </p>

                <p
                  *ngIf="booking.ship.receptionTel"
                  class="text-xs text-gray-600"
                >
                  <span class="font-semibold text-xs">Reception Tel:</span
                  ><br />
                  {{ booking.ship.receptionTel }}
                </p>
                <p *ngIf="booking.ship.shipEmail" class="text-xs text-gray-600">
                  <span class="font-semibold text-xs">Ship Email:</span><br />
                  {{ booking.ship.shipEmail }}
                </p>
              </div>
              <!-- Duration -->
              <div class="mt-1">
                <p class="text-xs text-gray-600">
                  <span class="font-semibold text-xs">Tourcode:</span>
                  {{ booking.duration.tourCode }}
                </p>
                <p class="text-xs text-gray-600">
                  <span class="font-semibold text-xs">Datum:</span>
                  {{
                    booking.duration.date | timestampToDate | date: 'dd.MM.YYYY'
                  }}
                </p>
                <p class="text-xs text-gray-600">
                  <span class="font-semibold text-xs">Start Zeit:</span>
                  {{
                    booking.duration.startTime
                      | timestampToDate
                      | date: 'HH:mm' : 'UTC'
                  }}
                </p>
                <p class="text-xs text-gray-600">
                  <span class="font-semibold text-xs">End Zeit:</span>
                  {{
                    booking.duration.endTime
                      | timestampToDate
                      | date: 'HH:mm' : 'UTC'
                  }}
                </p>
              </div>
            </div>
          </div>

          <!-- Tour Information -->
          <div *ngIf="booking.cruiseDetails" class="mb-6">
            <h2 class="text-base font-bold text-gray-800">
              Cruise Information
            </h2>
            <div class="mt-2 flex flex-col">
              <p class="text-xs text-gray-600">
                <span class="font-semibold text-xs">Manager:</span><br />
                {{ booking.cruiseDetails.cruiseManager }}
              </p>
              <p class="text-xs text-gray-600">
                <span class="font-semibold text-xs">Manager Tel:</span>
                {{ booking.cruiseDetails.cruiseManagerTel }}
              </p>
              <p class="text-xs text-gray-600">
                <span class="font-semibold text-xs">Anlegestelle:</span><br />
                {{ getJettyValue(booking.cruiseDetails.jetty) }}
              </p>
              <p class="text-xs text-gray-600">
                <span class="font-semibold text-xs">Pax Schiff:</span>
                {{ booking.cruiseDetails.paxShip }}
              </p>
              <p class="text-xs text-gray-600">
                <span class="font-semibold text-xs">Pax Tour:</span>
                {{ booking.cruiseDetails.paxTour }}
              </p>
              <p class="text-xs text-gray-600">
                <span class="font-semibold text-xs">Interne Notiz:</span>
                {{ booking.cruiseDetails.internNote }}
              </p>
              <p class="text-xs text-gray-600">
                <span class="font-semibold text-xs">Rechnungs Nr.:</span>
                {{ booking.cruiseDetails.invoiceNumber }}
              </p>
            </div>
          </div>

          <!-- Bearbeitungs Infos -->
          <div class="mt-1 flex flex-col">
            <h2 class="text-base font-bold text-gray-800">
              Dokument Information
            </h2>
            <div class="mt-2">
              <p class="text-xs text-gray-600">
                <span class="font-semibold text-xs">Buchungs ID:</span><br />
                {{ booking.id }}
              </p>
              <p class="text-xs text-gray-600">
                <span class="font-semibold text-xs">Erstellt:</span>
                {{ booking.createdDate | timestampToDate | date: 'dd.MM.YYYY' }}
              </p>
              <p class="text-xs text-gray-600">
                <span class="font-semibold text-xs">Erstellter:</span><br />
                {{ booking.userCreatorName }}
              </p>
            </div>
            <div *ngIf="getLatestBookingChange(booking) as latestChange">
              <p class="text-xs text-gray-600">
                <span class="font-semibold text-xs">Bearbeitet:</span>
                {{
                  latestChange.timestamp
                    ? (latestChange.timestamp
                      | timestampToDate
                      | date: 'dd.MM.YYYY')
                    : 'Kein Datum verfügbar'
                }}
              </p>
              <p class="text-xs text-gray-600">
                <span class="font-semibold text-xs">Bearbeiter:</span><br />
                {{ latestChange.changeAuthor.name }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="flex flex-row justify-start gap-x-6">
        <!-- Bus Information -->
        <div *ngIf="booking.busInfos" class="mb-6">
          <h2 class="text-base font-bold text-gray-800 whitespace-nowrap">
            Bus Information
          </h2>
          <div class="mt-4">
            <p class="text-xs text-gray-600">
              <span class="text-xs font-semibold">Busunternehmen:</span><br />
              {{ booking.busInfos.busCompany }}
            </p>
            <div class="flex flex-row">
              <p class="text-xs text-gray-600 font-semibold">Busse:</p>
              <ul class="list-disc pl-6 text-xs text-gray-600">
                <ng-container
                  *ngFor="
                    let entry of booking.busInfos.selectedBuses | keyvalue
                  "
                >
                  <li *ngIf="entry.value > 0">
                    {{ entry.key }}: {{ entry.value }}
                  </li>
                </ng-container>
              </ul>
            </div>
          </div>
        </div>

        <!-- Driver Information -->
        <div
          *ngIf="
            booking.driverInfo && checkForDrRichard(booking);
            else driverListing
          "
          class="mb-6"
        >
          <h2 class="text-base font-bold text-gray-800">Lenker Information</h2>
          <div class="mt-4">
            <p class="text-xs text-gray-600">
              <span class="text-xs font-semibold">URL:</span>
              {{ booking.driverInfo.url }}
            </p>
            <p class="text-xs text-gray-600">
              <span class="text-xs font-semibold">Kundennr.:</span>
              {{ booking.driverInfo.customerNumber }}
            </p>
            <p class="text-xs text-gray-600">
              <span class="text-xs font-semibold">Auftragsnr.:</span>
              {{ booking.driverInfo.orderNumber }}
            </p>
          </div>
        </div>
        <ng-template #driverListing>
          <div *ngIf="booking.driverInfo && booking.driverInfo.drivers">
            <h2 class="text-base font-bold text-gray-800">
              Lenker Information
            </h2>
            <div class="flex flex-wrap w-full justify-between">
              <div
                *ngFor="let driver of booking.driverInfo.drivers | keyvalue"
                class="w-1/4 p-2"
              >
                <div class="mt-2 flex flex-col">
                  <p class="text-xs text-gray-600">
                    <span class="text-xs font-semibold">Lenkername:</span><br />
                    {{ driver.value.driverName }}
                  </p>
                  <p class="text-xs text-gray-600">
                    <span class="text-xs font-semibold whitespace-nowrap"
                      >Lenker Tel:</span
                    ><br />
                    <span class="whitespace-nowrap">{{
                      driver.value.driverTel
                    }}</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </ng-template>
        <div
          *ngIf="
            booking.accounting?.billing?.billed ||
            booking.accounting?.accountingNote ||
            booking.accountingNote
          "
          class="flex flex-row justify-between gap-x-6"
        >
          <!-- Links: Billing Status -->
          <div class="flex flex-col">
            <h2 class="text-base font-bold text-gray-800">Abrechnung</h2>
            <div class="mt-2 flex items-center gap-x-2">
              <span
                class="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium"
                [ngClass]="
                  booking.accounting?.billing?.billed
                    ? 'bg-green-100 text-green-700'
                    : 'bg-yellow-100 text-yellow-700'
                "
              >
                {{
                  booking.accounting?.billing?.billed ? 'Abgerechnet' : 'Offen'
                }}
              </span>
              <span
                *ngIf="booking.accounting?.billing?.billed"
                class="text-xs text-gray-500"
              >
                {{ booking.accounting?.billing?.userBilledName }} am
                {{
                  booking.accounting?.billing?.dateBilled?.toDate()
                    | date: 'dd.MM.yyyy HH:mm'
                }}
              </span>
            </div>
          </div>

          <!-- Rechts: Accounting Note -->
          <div
            *ngIf="booking.accounting?.accountingNote || booking.accountingNote"
            class="flex flex-col flex-shrink-0 max-w-[50%]"
          >
            <h2 class="text-base font-bold text-gray-800">Accounting Note</h2>
            <p class="mt-2 text-xs text-gray-600 whitespace-pre-wrap ">
              {{ booking.accounting?.accountingNote || booking.accountingNote }}
            </p>
          </div>
        </div>
      </div>

      <!-- Guides -->
      <div *ngIf="booking.guides" class="mb-6">
        <h2 class="text-base font-bold text-gray-800">Guides</h2>
        <div
          class="mt-4"
          *ngFor="let guide of displayGuides"
          [ngClass]="{
            'bg-red-200/75 border-red-900 border-4 ': guide.storno.isActive,
          }"
        >
          <table class="w-full border border-gray-200">
            <thead>
              <tr
                class="bg-gray-100"
                [class.bg-red-400]="guide.storno.isActive"
              >
                <th class="border px-4 py-2 text-left text-xs">Name</th>
                <th class="border px-4 py-2 text-left text-xs">Tel</th>
                <th class="border px-4 py-2 text-left text-xs">Email</th>
                <th class="border px-4 py-2 text-left text-xs">Stellzeit</th>
                <th class="border px-4 py-2 text-left text-xs">Ort</th>
                <th class="border px-4 py-2 text-left text-xs">Tour</th>
                <th class="border px-4 py-2 text-left text-xs">❓</th>
                <th class="border px-4 py-2 text-left text-xs">Sprachen</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="border px-4 py-2 text-xs">
                  {{ guide.guideDetails.forename }}
                  {{ guide.guideDetails.surname }}
                </td>
                <td class="border px-4 py-2 text-xs">
                  {{ guide.guideDetails.tel }}
                </td>
                <td class="border px-4 py-2 text-xs">
                  {{ guide.guideDetails.email }}
                </td>
                <td class="border px-4 py-2 text-xs">
                  {{
                    guide.positionDetails.time
                      | timestampToDate
                      | date: 'HH:mm' : 'UTC'
                  }}
                </td>
                <td class="border px-4 py-2 text-xs">
                  {{ guide.positionDetails.location }}
                </td>
                <td class="border px-4 py-2 text-xs">
                  {{ guide.tour.tourInfo.tourName || guide.tour.tourInfo }}
                </td>
                <td class="border px-4 py-2 text-xs">
                  @if (guide.tour.tourConfirm) {
                    ✅
                  } @else if (guide.storno.isActive) {
                    ❌
                  } @else {
                    🟡
                  }
                </td>
                <td class="border px-4 py-2 text-xs">
                  <ul class="list-disc pl-4">
                    <li
                      *ngFor="
                        let language of getActiveLanguages(
                          guide.guideDetails.languages
                        )
                      "
                    >
                      {{ language }}
                    </li>
                  </ul>
                </td>
              </tr>
            </tbody>
          </table>
          <ng-container *ngIf="guide.other && checkOtherLength(guide.other)">
            <table class="w-full border border-gray-200">
              <thead>
                <tr
                  class="bg-gray-100"
                  [class.bg-red-400]="guide.storno.isActive"
                >
                  <th
                    *ngFor="let other of guide.other | keyvalue; index as i"
                    class="border px-4 py-2 text-left text-xs"
                  >
                    Sonstiges {{ i + 1 }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <ng-container *ngFor="let other of guide.other | keyvalue">
                    <td class="border px-4 py-2 text-xs">
                      {{ other.value }}
                    </td>
                  </ng-container>
                </tr>
              </tbody>
            </table>
          </ng-container>
          <ng-container *ngIf="guide.storno.isActive">
            <div class="px-4 flex w-full flex-row italic">
              <p class="font-bold text-red-900 pr-2 text-xs">Storno:</p>
              <ng-container
                *ngIf="guide.storno.storno === 'other'; else stornoMap"
              >
                <p class="text-xs">
                  {{ guide.storno.customStorno }}
                </p>
              </ng-container>
              <ng-template #stornoMap>
                <p class="text-xs">
                  {{ getStornoValue(guide.storno.storno) || 'Unbekannt' }}
                </p>
              </ng-template>
            </div>
          </ng-container>
        </div>
      </div>
      <div class="flex flex-row justify-between">
        <div
          *ngIf="booking.rundown"
          class="flex flex-col justify-start gap-x-6"
        >
          <h2 class="text-base font-bold text-gray-800">Rundown</h2>
          <div class="mt-4">
            <p class="text-xs text-gray-600 whitespace-pre-wrap">
              {{ booking.rundown }}
            </p>
          </div>
        </div>
        <div
          *ngIf="booking.voucherAddonText"
          class="flex flex-col justify-start gap-x-6"
        >
          <h2 class="text-base font-bold text-gray-800">Voucher Addon Text</h2>
          <div class="mt-4">
            <p class="text-xs text-gray-600 whitespace-pre-wrap">
              {{ booking.voucherAddonText }}
            </p>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: ``,
  imports: [CommonModule, TimestampToDatePipe],
})
export class BookingPdfHtmlComponent implements OnInit {
  @Input({ required: true }) booking!: Booking;
  stornoMap = StornoMap;
  jettyMap = JettyMap;
  busCompanyMap = BusCompanyMap;
  displayGuides: newGuideInterface[] = [];
  ngOnInit(): void {
    if (this.booking.guides && this.booking.guideOrder) {
      this.displayGuides = sortGuides(
        this.booking.guides,
        this.booking.guideOrder,
      );
    }

    //  console.log(this.booking.guides);
  }

  checkForDrRichard(booking: Booking): boolean {
    return booking.busInfos?.busCompany === BusCompanyMap['Dr_Richard'];
  }

  getLatestBookingChange(booking: Booking): BookingChange | undefined {
    return getLatestChange(booking);
  }

  getStornoValue(val: string): string {
    if (!val) return 'N/A';

    // Case 1: direct key lookup
    if (this.stornoMap[val]) {
      return this.stornoMap[val];
    }

    // Case 2: search by value
    const entry = Object.entries(this.stornoMap).find(([_, v]) => v === val);
    if (entry) {
      return entry[1];
    }

    // Case 3: not found
    return 'N/A';
    return this.stornoMap[val];
  }

  getJettyValue(val: string): string {
    if (!val) return 'N/A';

    // Case 1: direct key lookup
    if (this.jettyMap[val]) {
      return this.jettyMap[val];
    }

    // Case 2: search by value
    const entry = Object.entries(this.jettyMap).find(([_, v]) => v === val);
    if (entry) {
      return entry[1];
    }

    // Case 3: not found
    return 'N/A';
  }

  getActiveLanguages(languages: Record<string, boolean>): string[] {
    return Object.keys(languages).filter((language) => languages[language]);
  }

  checkOtherLength(other: Record<string, string>) {
    if (Object.keys(other).length === 0) {
      return false;
    } else {
      return true;
    }
  }

  otherNumber(value: string) {
    if (value && !isNaN(Number(value))) {
      return Number(value) + 1;
    } else {
      return 'undefined';
    }
  }
}
